import { createContext, FunctionComponent } from 'preact';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import type { Player } from '@schibsted-svp/web-player';

import { PlaylistItemManager } from '../utils/PlaylistItemManager';
import {
  isAutoRetryableError,
  isRecoverableError,
} from '../utils/errorRecoverability';
import { getRawAsset } from '../../../utils/player/asset';

import { usePlayer } from './PlayerProvider';
import { usePlayerEvent } from './usePlayerEvent';
import { usePlayerState } from './usePlayerState';
import { useJWPlayerEvent } from './useJWPlayerEvent';
import { usePlayerError } from './PlayerErrorProvider';
import { useTracker } from './useTracker';

export interface RecoveryMetadata {
  prerollComplete: boolean;
  assetId?: number;
  lastKnownPosition: number;
  isPlaying: boolean;
}

const ErrorRecoveryContext = createContext<{
  recover: () => Promise<void>;
  handleRetry: () => void;
  handleReload: () => void;
  canRecover: boolean;
  isAutoRetrying: boolean;
  metadata: RecoveryMetadata;
}>({
  recover: () => Promise.resolve(),
  handleRetry: () => {},
  handleReload: () => {},
  canRecover: false,
  isAutoRetrying: false,
  metadata: {
    prerollComplete: false,
    lastKnownPosition: 0,
    isPlaying: false,
  },
});

const RECOVER_FROM_ERROR_THRESHOLD_MS = 3000;
const MAX_AUTO_RETRIES = 3;

const isPlayingState = (state: ReturnType<Player['getState']>): boolean => {
  return ['playing', 'buffering', 'loading'].includes(state);
};

export const ErrorRecoveryProvider: FunctionComponent = ({ children }) => {
  // Player might be null if error occured during player's init phase
  const player: Player | null = usePlayer();
  const currentState = usePlayerState();
  const { errorDetails } = usePlayerError();
  const metadataRef = useRef<RecoveryMetadata>({
    prerollComplete: false,
    assetId: getRawAsset(player)?.id,
    lastKnownPosition: 0,
    isPlaying: player?.getJWPlayer()
      ? isPlayingState(player?.getState())
      : false,
  });
  const lastRecoveryTimeRef = useRef(0);
  const autoRetryCountRef = useRef(0);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);

  const canRecover = isRecoverableError(errorDetails?.code);

  useEffect(() => {
    if (isPlayingState(currentState)) {
      metadataRef.current.isPlaying = true;
    }
    if (currentState === 'paused') {
      metadataRef.current.isPlaying = false;
    }
    // ignore other states
  }, [currentState]);

  const handleNewAsset = useCallback(() => {
    const asset = getRawAsset(player);
    if (asset && asset.id !== metadataRef.current.assetId) {
      lastRecoveryTimeRef.current = 0;
      autoRetryCountRef.current = 0;
      metadataRef.current.lastKnownPosition = 0;
      metadataRef.current.assetId = asset.id;
      metadataRef.current.prerollComplete = false;
      metadataRef.current.isPlaying = player?.getJWPlayer()
        ? isPlayingState(player?.getState())
        : false;
    }
  }, [player]);

  const handleTime = useCallback(({ position }: jwplayer.TimeParam) => {
    metadataRef.current.lastKnownPosition = position;
  }, []);

  const handleAdBreakEnd = useCallback(({ offset }: jwplayer.AdBreakParam) => {
    if (offset === 'pre') {
      metadataRef.current.prerollComplete = true;
    }
  }, []);

  const handleRecovered = useCallback(() => {
    autoRetryCountRef.current = 0;
    setIsAutoRetrying(false);
  }, []);

  const recover = useCallback(() => {
    const metadata = metadataRef.current;
    const runCallback = async () => {
      const jw = player?.getJWPlayer();
      if (!jw) {
        return;
      }

      const manager = PlaylistItemManager.fromPlaylist(jw);
      if (!manager) {
        return;
      }

      const now = Date.now();
      // Rate limit retries to one recovery attempt per 3s per asset
      if (lastRecoveryTimeRef.current > now - RECOVER_FROM_ERROR_THRESHOLD_MS) {
        return;
      }

      lastRecoveryTimeRef.current = now;

      const { isPlaying, lastKnownPosition, prerollComplete } = metadata;

      if (!manager.hasValidToken()) {
        await manager.refreshToken();
      }

      if (prerollComplete || lastKnownPosition > 0) {
        manager.removePrerollAds();
      }
      // These midrolls are removed in order to make sure no ads gets played twice
      // when at later point a playlistItem gets reloaded
      manager.removeMidrollAdsBeforeTime(lastKnownPosition);
      manager.setStartTime(lastKnownPosition);
      jw.load(manager.getItem());

      if (isPlaying) {
        jw.play();
      }

      player?.trigger('recovered', { metadata });
    };

    return runCallback();
  }, [player]);

  const playerErrorHandler = useCallback(
    (error: { code: number }) => {
      if (
        isAutoRetryableError(error.code) &&
        autoRetryCountRef.current < MAX_AUTO_RETRIES
      ) {
        autoRetryCountRef.current += 1;
        setIsAutoRetrying(true);
        recover();
      }
    },
    [recover]
  );

  const handleAccessGranted = useCallback(() => {
    // 15103 - NOT_AUTHENTICATED
    if (errorDetails?.code === 15103) {
      recover();
    }
  }, [errorDetails?.code, recover]);

  useJWPlayerEvent('adBreakEnd', handleAdBreakEnd);
  usePlayerEvent('assetReady', handleNewAsset);
  usePlayerEvent('playlistItem', handleNewAsset);
  useJWPlayerEvent('time', handleTime);
  usePlayerEvent('error', playerErrorHandler);
  usePlayerEvent('recovered', handleRecovered);
  usePlayerEvent('accessGranted', handleAccessGranted);

  const { trackButtonClick } = useTracker();

  const handleRetry = useCallback(() => {
    trackButtonClick('retry-on-error');
    recover();
  }, [trackButtonClick, recover]);

  const handleReload = useCallback(() => {
    trackButtonClick('reload-on-error');
    window.location.reload();
  }, [trackButtonClick]);

  const value = useMemo(
    () => ({
      recover,
      handleRetry,
      handleReload,
      canRecover,
      isAutoRetrying,
      metadata: metadataRef.current,
    }),
    [recover, handleRetry, handleReload, canRecover, isAutoRetrying]
  );

  return (
    <ErrorRecoveryContext.Provider value={value}>
      {children}
    </ErrorRecoveryContext.Provider>
  );
};

export const useErrorRecovery = () => {
  const value = useContext(ErrorRecoveryContext);

  if (process.env.NODE_ENV !== 'production' && !value) {
    console.warn(
      'Player error context is undefined, please verify you are calling useErrorRecovery() as child of a <ErrorRecoveryProvider> component.'
    );
  }

  return value;
};
